import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useCardBackground } from './SectionSurfaceContext';
import { interactionCard } from './styles/contentBlocks';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';

const THANK_YOU_HEIGHT_PX = 114;
const SHRINK_DURATION_MS = 350;

export interface ThankYouMessageProps {
  visible: boolean;
}

export function ThankYouMessage({
  visible,
}: ThankYouMessageProps): React.ReactElement | null {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const cardBackground = useCardBackground();
  const prefersReducedMotion = usePrefersReducedMotion();
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const [animationPhase, setAnimationPhase] = useState<'measuring' | 'shrinking' | 'done'>('measuring');

  useEffect(() => {
    if (!visible) {
      setAnimationPhase('measuring');
      setMeasuredHeight(null);
      return;
    }

    const el = innerRef.current;
    if (!el) return;

    const currentHeight = el.getBoundingClientRect().height;
    const needsShrink = currentHeight > THANK_YOU_HEIGHT_PX;

    if (!needsShrink || prefersReducedMotion) {
      setAnimationPhase('done');
      return;
    }

    setMeasuredHeight(currentHeight);
    setAnimationPhase('shrinking');

    const timer = window.setTimeout(() => {
      setAnimationPhase('done');
    }, SHRINK_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [visible, prefersReducedMotion]);

  if (!visible) return null;

  const shouldAnimate = animationPhase === 'shrinking' && measuredHeight !== null;
  const isFinal = animationPhase === 'done' || shouldAnimate;

  const cardStyle: React.CSSProperties = {
    // radius.xl === '15px' — same rendered radius as the previous hardcoded value.
    ...interactionCard(tokens),
    backgroundColor: cardBackground ?? tokens.backgrounds.cardAlt,
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    height: isFinal ? `${THANK_YOU_HEIGHT_PX}px` : undefined,
    transition: shouldAnimate
      ? `height ${SHRINK_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
      : undefined,
  };

  const textStyle: React.CSSProperties = {
    fontFamily: tokens.typography.fontFamily,
    fontSize: tokens.typography.body.size,
    fontWeight: 500,
    color: tokens.text.muted,
    textAlign: 'center',
    padding: `${tokens.spacing.lg} ${tokens.spacing["2xl"]}`,
  };

  return (
    <div ref={innerRef} style={cardStyle} data-testid="thank-you-message">
      <span style={textStyle}>{t('question_feedback.response_recorded')}</span>
    </div>
  );
}
